import { useEffect, useMemo, useRef, useState } from "react";

import { Box, Button, Card, Checkbox, Collapse, Fade, Grid, IconButton, Stack, Typography } from "@mui/material";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";

import { DESTINATION, LAYOUT_MARGIN, SOURCE } from "Configs/home";

import { useAppDispatch, useAppSelector } from "Hooks/useRedux";
import useWindowSize from "Hooks/useWindowSize";
import { IPath } from "Types/path";

import { deselectPath } from "Redux/servicesSlice";
import { setMapData } from "Redux/mapSlice";
import usePaths from "Hooks/usePaths";
import useEditPath from "Hooks/useEditPath";
import { changeShowReportRightControl, changeShowPaths } from "Redux/sideSlice";

// Doesn't look good but it works :(
const SIZES: { [key: string]: number } = {
  operationBoxHeight: 56,
  marginTop: 8,
  toolsBoxHeight: 56,
  marginBottom: 8,
  topContent: 70,
  bottomContent: 56,
  bottomContentMargin: 16,
  toolsBoxPadding: 8,
};

export default function ShowPaths() {
  const dispatch = useAppDispatch();

  const { name } = useAppSelector((state) => state.operation);
  const { visible, deselectedPathes } = useAppSelector((state) => state.services);
  const data = usePaths();
  const { editPath, showPathOnMap } = useEditPath();

  const { height } = useWindowSize();

  const [isOpen, setIsOpen] = useState<boolean>(true);

  const toggleCollapse = () => setIsOpen((prev) => !prev);

  const sideRef = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState<number>(0);

  const handleClick = (e: any, path: IPath) => {
    if (sideRef?.current) {
      dispatch(changeShowReportRightControl(true));
      dispatch(changeShowPaths(false));
      setScroll(sideRef.current.scrollTop);
      const stationsLength = path?.stations?.length || 1;
      dispatch(
        setMapData({
          bounds: {
            positions: [
              [path.stations[0].lat, path.stations[0].lng],
              [path.stations[stationsLength - 1].lat, path.stations[stationsLength - 1].lng],
            ],
            options: { maxZoom: 13, animate: true, duration: 0.5 },
          },
        })
      );
      editPath(path);
    }
  };

  const boxHeight = useMemo(() => {
    let result = 0;
    Object.keys(SIZES).forEach((key) => {
      result += SIZES[key];
    });
    return `${height - (result + LAYOUT_MARGIN * 2)}px`;
  }, [height]);

  const handleDeselect = (e: React.ChangeEvent<HTMLInputElement>, path: IPath) => {
    let newDeselectedPathes: number[] = [];
    if (!e.target.checked) {
      newDeselectedPathes = [...deselectedPathes, path.id];
    } else {
      const stationsLength = path?.stations?.length || 1;
      dispatch(
        setMapData({
          bounds: {
            positions: [
              [path.stations[0].lat, path.stations[0].lng],
              [path.stations[stationsLength - 1].lat, path.stations[stationsLength - 1].lng],
            ],
            options: { maxZoom: 13, animate: true, duration: 0.5 },
          },
        })
      );
      newDeselectedPathes = deselectedPathes.filter((id) => id !== path.id);
    }
    dispatch(deselectPath(newDeselectedPathes));
  };

  const handleAllChecked = (isChecked: boolean) => {
    if (!isChecked) return dispatch(deselectPath(data?.data?.map((path) => path.id) || []));
    dispatch(deselectPath([]));
  };

  useEffect(() => {
    sideRef?.current?.scrollTo({
      top: scroll,
      left: 0,
      behavior: "auto",
    });
  }, [name]);

  const renderPaths = () => {
    return (
      <>
        <Stack direction="row" sx={{ mb: "20px", alignItems: "center" }}>
          <Typography sx={{ width: "90%" }}>انتخاب همه :</Typography>
          <Checkbox
            inputProps={{ "aria-label": "Checkbox demo" }}
            sx={{
              "&:hover": { bgcolor: "transparent" },
            }}
            onChange={(e) => handleAllChecked(e.target.checked)}
            checked={deselectedPathes.length === 0}
            indeterminate={deselectedPathes.length !== data?.data?.length && deselectedPathes.length > 0}
          />
        </Stack>
        {data?.data?.map((path, index) => {
          return (
            <Stack direction="row">
              <Button
                sx={{
                  border: "1px solid #aaa",
                  p: 1,
                  borderRadius: 1,
                  width: "90%",
                  marginBottom: "15px",
                  justifyContent: "flex-start",
                }}
                onClick={(e) => handleClick(e, path)}
              >
                <Stack sx={{ width: "100%" }}>
                  <Grid item xs={9} textAlign="left" sx={{ mb: "10px" }}>
                    <Typography color="common.black">{path.name}</Typography>
                  </Grid>
                  <Grid item xs={9} textAlign="right">
                    <Stack direction="row">
                      <img src={SOURCE} alt="logo" style={{ width: "15px" }} />
                      <Typography color="common.black" sx={{ ml: "10px" }}>
                        از :
                      </Typography>
                      <Typography color="common.black" sx={{ ml: "10px" }}>
                        {path?.stations[0]?.title}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={9} textAlign="right">
                    <Stack direction="row">
                      <Stack direction="row" sx={{ width: "65%" }}>
                        <img src={DESTINATION} alt="logo" style={{ width: "15px" }} />
                        <Box color="common.black" sx={{ ml: "10px", textOverflow: "ellipsis", overflow: "hidden" }}>
                          به : {path?.stations[Number(path?.stations?.length) - 1]?.title}
                        </Box>
                      </Stack>
                      <Stack direction="row" sx={{ ml: "10px", width: "30%", flexDirection: "row-reverse" }}>
                        <Typography color="#484242" sx={{ ml: "10px" }}>
                          {path.code}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Grid>
                </Stack>
              </Button>
              <Checkbox
                inputProps={{ "aria-label": "Checkbox demo" }}
                sx={{
                  "&:hover": { bgcolor: "transparent" },
                }}
                onChange={(e) => handleDeselect(e, path)}
                checked={!deselectedPathes.includes(path.id)}
              />
            </Stack>
          );
        })}
      </>
    );
  };

  return (
    <Fade in={visible && !name} mountOnEnter unmountOnExit>
      <Card sx={{ mt: 1, p: 2, width: "100%", boxShadow: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight="800">لیست مسیرها</Typography>

          <Button
            startIcon={<KeyboardDoubleArrowDownIcon sx={{ transform: isOpen ? "rotate(180deg)" : "", transition: "all 0.3s" }} />}
            sx={{ minWidth: "100px" }}
            onClick={toggleCollapse}
          >
            {isOpen ? "بستن" : "بازکردن"}
          </Button>
        </Stack>

        <Collapse in={isOpen}>
          <Box sx={{ height: boxHeight, py: 2, overflowY: "scroll" }} ref={sideRef}>
            {data?.data?.length === 0 ? <Typography align="center">مسیری برای نمایش وجود ندارد</Typography> : renderPaths()}
          </Box>
        </Collapse>
      </Card>
    </Fade>
  );
}
